import { useState, useEffect } from 'react'
import { getUserReviews, getUserReviewSummary, formatTimeAgo } from '../lib/reviewService'
import { Review, ReviewSummary } from '../types/review'
import ReviewsModal from './ReviewsModal'

interface ReviewsSectionProps {
  userId: string
}

export default function ReviewsSection({ userId }: ReviewsSectionProps) {
  const [reviews, setReviews] = useState<Review[]>([])
  const [allReviews, setAllReviews] = useState<Review[]>([])
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null)
  const [reviewsLoading, setReviewsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Inline SVG star renderer
  const renderStars = (rating: number) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill={star <= rating ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={star <= rating ? 'text-amber-400' : 'text-white/15'}
        >
          <path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/>
        </svg>
      ))}
    </div>
  )

  // Load reviews data
  const loadReviews = async () => {
    setReviewsLoading(true)
    try {
      const [allReviewsData, summaryData] = await Promise.all([
        getUserReviews(userId, 100),
        getUserReviewSummary(userId)
      ])

      setAllReviews(allReviewsData)
      setReviews(allReviewsData.slice(0, 4))
      setReviewSummary(summaryData)
    } catch (err) {
      console.error('Error loading reviews:', err)
    } finally {
      setReviewsLoading(false)
    }
  }

  useEffect(() => {
    if (userId) {
      loadReviews()
    }
  }, [userId])

  // Get initials from name
  const getInitials = (name: string) => {
    const parts = name.trim().split(' ')
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    if (parts.length === 1 && parts[0].length > 0) return parts[0].substring(0, 2).toUpperCase()
    return 'U'
  }

  const renderReviewCard = (review: Review) => (
    <div key={review.id} className="flex items-start gap-3.5">
      {review.reviewerPhotoUrl ? (
        <img
          src={review.reviewerPhotoUrl}
          alt={review.reviewerName}
          className="h-9 w-9 rounded-full object-cover shrink-0"
        />
      ) : (
        <div
          className="h-9 w-9 rounded-full shrink-0 grid place-items-center text-[11px] font-semibold"
          style={{ background: 'rgba(255,255,255,0.08)', boxShadow: 'inset 0 0 0 1px hsl(0 0% 25%)' }}
        >
          {getInitials(review.reviewerName)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13px] font-medium truncate">{review.reviewerName}</span>
          <span className="text-[11px] text-white/35 shrink-0">{formatTimeAgo(review.createdAt)}</span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          {renderStars(review.rating)}
          {review.isVerified && (
            <span className="text-[10px] text-white/40 flex items-center gap-1">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
              Verified
            </span>
          )}
        </div>
        <p className="text-[12.5px] text-white/70 leading-relaxed mt-2">{review.comment}</p>
      </div>
    </div>
  )

  return (
    <>
      <div
        className="mt-6 rounded-2xl overflow-hidden"
        style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border) / 0.6)' }}
      >
        <div className="p-5 sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="text-white/50">
                <path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/>
              </svg>
              <h3 className="text-[15px] font-medium tracking-tight-half">Reviews</h3>
            </div>
            {reviewSummary && reviewSummary.totalReviews > 0 && (
              <div className="flex items-center gap-2">
                {renderStars(Math.round(reviewSummary.averageRating))}
                <span className="text-[12px] text-white/50">
                  {reviewSummary.averageRating.toFixed(1)} · {reviewSummary.totalReviews}
                </span>
              </div>
            )}
          </div>

          {/* Loading */}
          {reviewsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white/30"></div>
              <span className="ml-3 text-white/40 text-[12.5px]">Loading reviews...</span>
            </div>

          /* Empty state */
          ) : reviews.length === 0 ? (
            <div className="text-center py-10">
              <svg className="mx-auto w-8 h-8 text-white/15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/>
              </svg>
              <h4 className="mt-3 text-[13px] font-medium text-white/80">No reviews yet</h4>
              <p className="mt-1 text-[12px] text-white/40">
                Reviews will appear here after completed sessions.
              </p>
            </div>

          /* Reviews content */
          ) : (
            <div>
              {/* Rating summary bar */}
              {reviewSummary && (
                <div
                  className="rounded-xl p-4 mb-5"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(var(--border) / 0.5)' }}
                >
                  <div className="flex items-center gap-5">
                    {/* Big number */}
                    <div className="text-center shrink-0">
                      <div className="text-[28px] font-semibold tracking-tight">{reviewSummary.averageRating.toFixed(1)}</div>
                      <div className="mt-1">{renderStars(Math.round(reviewSummary.averageRating))}</div>
                      <div className="text-[11px] text-white/40 mt-1.5">
                        {reviewSummary.totalReviews} review{reviewSummary.totalReviews !== 1 ? 's' : ''}
                      </div>
                    </div>

                    {/* Distribution bars */}
                    <div className="flex-1 space-y-1.5">
                      {[5, 4, 3, 2, 1].map((rating) => {
                        const count = reviewSummary.ratingDistribution[rating as keyof typeof reviewSummary.ratingDistribution]
                        const pct = reviewSummary.totalReviews > 0 ? (count / reviewSummary.totalReviews) * 100 : 0
                        return (
                          <div key={rating} className="flex items-center gap-2.5">
                            <span className="text-[11px] text-white/40 w-3 text-right">{rating}</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="text-amber-400 shrink-0">
                              <path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/>
                            </svg>
                            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'hsl(0 0% 15%)' }}>
                              <div
                                className="h-full rounded-full bg-amber-400/80 transition-all duration-500"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-[11px] text-white/30 w-4 text-right">{count}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Recent reviews */}
              <div className="space-y-0">
                {reviews.map((review, index) => (
                  <div
                    key={review.id}
                    className={index !== reviews.length - 1 ? 'pb-4 mb-4' : ''}
                    style={index !== reviews.length - 1 ? { borderBottom: '1px solid hsl(0 0% 15%)' } : undefined}
                  >
                    {renderReviewCard(review)}
                  </div>
                ))}
              </div>

              {/* View all button */}
              {reviewSummary && allReviews.length > 4 && (
                <div className="pt-4 mt-4" style={{ borderTop: '1px solid hsl(0 0% 15%)' }}>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="btn btn-outline w-full justify-center text-[12.5px]"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/>
                    </svg>
                    View all {reviewSummary.totalReviews} reviews
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reviews Modal */}
      <ReviewsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        reviews={allReviews}
        reviewSummary={reviewSummary}
        renderStars={renderStars}
        renderReviewCard={renderReviewCard}
      />
    </>
  )
}
